import cn from '@pkgs/shared-client/helpers/cn';
import { scrollToElement } from '@pkgs/shared-client/helpers/dom';
import useElementBoudingClientRectUpdated from '@pkgs/shared-client/hooks/useElementBoudingClientRectUpdated';
import useEventCallback from '@pkgs/shared-client/hooks/useEventCallback';
import { useEffect, useMemo, useRef, useState } from 'react';
import SVGrid, { type LikeItem } from './SVGrid';
import SVGridCardItem, {
	CARD_BOTTOM_PADDING_CLASSES,
	CARD_WIDTH_CLASSES,
	createCardElementIDFromShortID,
} from './SVGridCardItem';
import SVLoadingIndicator from './SVLoadingIndicator';
import SVPageMargins from './SVPageMargins';
import SVVirtualList from './SVVirtualList';

const CardSkeleton = () => (
	<div className={cn('h-[75dvh] overflow-hidden', CARD_WIDTH_CLASSES)}>
		<div className="relative flex h-full flex-col">
			<div className="relative flex flex-1 items-center justify-center overflow-hidden rounded-[4px] bg-gray-950">
				<div className="h-full w-full animate-pulse bg-gray-950"></div>
			</div>
			<div className="py-2.5">
				<SVGridCardItem.DetailSkeleton />
			</div>
		</div>
	</div>
);

const EMPTY_MESSAGE = {
	[SVGrid.SOURCE_TYPES.TEAM_FEED]: 'Your team mates did not save anything yet.',
	[SVGrid.SOURCE_TYPES.BOARD]: 'There is nothing here yet.',
};

// Do an efficient search for the item that is closest to the middle of the screen
function findClosestItemIndex(items: LikeItem[]): number {
	const screenMiddleY = window.innerHeight / 2;
	let closest = { index: 0, offset: Infinity };

	// Check all items since they might not be sorted by distance
	for (let i = 0; i < items.length; i++) {
		const itemElement = document.querySelector<HTMLDivElement>(
			`#${createCardElementIDFromShortID(items[i].shortID)}`,
		);

		if (!itemElement) {
			continue;
		}

		const rect = itemElement.getBoundingClientRect();
		const offsetFromCenterScreen = Math.abs(rect.top + rect.height / 2 - screenMiddleY);

		if (offsetFromCenterScreen < closest.offset) {
			closest = { index: i, offset: offsetFromCenterScreen };
		} else if (closest.offset !== Infinity && offsetFromCenterScreen > closest.offset) {
			// If the offset is greater than the closest, we can stop searching since it will only grow from here
			break;
		}
	}

	return closest.index;
}

function emptyMessageForSourceType(sourceType: ValueOf<typeof SVGrid.SOURCE_TYPES> | undefined) {
	if (sourceType && sourceType in EMPTY_MESSAGE) {
		return EMPTY_MESSAGE[sourceType as keyof typeof EMPTY_MESSAGE];
	}

	return 'There is nothing here yet.';
}

const SVGridCards = ({
	items,
	renderItem,
	keyExtractor,
	isLoading = false,
	isLoaded = false,
	loadingSkeletonCount = 3,
	onPaginate,
	sourceType,
	className,
	style,
}: {
	items: LikeItem[];
	renderItem: (item: LikeItem, height: number) => React.ReactNode;
	keyExtractor: (item: LikeItem) => string;
	isLoading?: boolean;
	isLoaded?: boolean;
	loadingSkeletonCount?: number;
	onPaginate?: (() => void) | null;
	sourceType?: ValueOf<typeof SVGrid.SOURCE_TYPES>;
	className?: string;
	style?: React.CSSProperties;
}) => {
	const isEmpty = isLoaded && items.length === 0;
	const shouldShowLoadingIndicator = isLoading;
	const containerRef = useRef<HTMLDivElement>(null);
	const widthProbeRef = useRef<HTMLDivElement>(null);

	const focusItemBy = useEventCallback((direction: number) => {
		if (!items.length) {
			return;
		}

		const closestIndex = findClosestItemIndex(items);

		// Circular wrapping index
		const nextIndex = (items.length + (closestIndex + direction)) % items.length;

		const nextItemElement = document.querySelector<HTMLDivElement>(
			`#${createCardElementIDFromShortID(items[nextIndex].shortID)}`,
		);

		if (!nextItemElement) {
			return;
		}

		scrollToElement(nextItemElement, 0, true);
	});

	const handleKeyDown = useEventCallback((e: KeyboardEvent) => {
		if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
			e.preventDefault();

			focusItemBy(1);
		} else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
			e.preventDefault();

			focusItemBy(-1);
		}
	});

	useEffect(() => {
		window.addEventListener('keydown', handleKeyDown);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [handleKeyDown]);

	const [cardMetrics, setCardMetrics] = useState<{
		windowHeight: number;
		top: number;
		width: number;
	}>({
		windowHeight: typeof window !== 'undefined' ? window.innerHeight : 0,
		top: 0,
		width: 0,
	});

	useElementBoudingClientRectUpdated(widthProbeRef, () => {
		if (!widthProbeRef.current) {
			return;
		}

		const rect = widthProbeRef.current.getBoundingClientRect();
		const windowHeight = window.innerHeight;

		const topPageY = window.scrollY + rect.top;

		if (
			cardMetrics.top === topPageY &&
			cardMetrics.width === rect.width &&
			cardMetrics.windowHeight === windowHeight
		) {
			return;
		}

		setCardMetrics({
			windowHeight,
			top: topPageY,
			width: rect.width,
		});
	});

	const heights = useMemo(() => {
		if (cardMetrics.width === 0) {
			return [];
		}

		return items.map((item) => SVGridCardItem.calculateHeight(cardMetrics, item));
	}, [items, cardMetrics]);

	if (isLoading && !items?.length) {
		return (
			<SVPageMargins>
				<div className="flex w-full flex-col items-center">
					{Array.from({ length: loadingSkeletonCount }).map((_, index) => (
						<div
							key={index}
							className="mb-[90px] w-full"
							style={{ display: 'flex', justifyContent: 'center' }}
						>
							<CardSkeleton />
						</div>
					))}
				</div>
			</SVPageMargins>
		);
	}

	const renderCardsList = () => {
		if (isEmpty) {
			return (
				<div className="py-8 text-center">
					<p>{emptyMessageForSourceType(sourceType)}</p>
				</div>
			);
		}

		if (items.length === 0) {
			return null;
		}

		return (
			<>
				<div className={cn(CARD_WIDTH_CLASSES, 'h-0')} ref={widthProbeRef} />
				<div className="relative flex-1">
					{cardMetrics.width > 0 ? (
						<SVVirtualList heights={heights} onPaginate={onPaginate} className="w-full">
							{items.map((item, index) => {
								return (
									<div
										key={keyExtractor(item)}
										className={cn(
											'flex w-full justify-center',
											CARD_BOTTOM_PADDING_CLASSES,
										)}
									>
										{renderItem(item, heights[index])}
									</div>
								);
							})}
						</SVVirtualList>
					) : null}
				</div>
			</>
		);
	};

	return (
		<SVPageMargins>
			<div
				ref={containerRef}
				className={cn('flex min-h-dvh w-full flex-col items-center', className)}
				style={style}
			>
				{renderCardsList()}
				{shouldShowLoadingIndicator && <SVLoadingIndicator />}
			</div>
		</SVPageMargins>
	);
};

export default SVGridCards;
